import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/core';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Link, Trash2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';

interface LinkPopoverProps {
  editor: Editor;
}

/** Popover for inserting, editing, and removing hyperlinks. Detects existing link at selection. */
export function LinkPopover({ editor }: LinkPopoverProps) {
  const { t } = useTranslation('richText');
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(true);

  const isActive = editor.isActive('link');

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setUrl(editor.getAttributes('link').href ?? '');
      setOpenInNewTab(editor.getAttributes('link').target === '_blank');
    }
    setOpen(nextOpen);
  };

  const handleApply = () => {
    if (!url.trim()) return;
    editor
      .chain()
      .focus()
      .setLink({ href: url.trim(), target: openInNewTab ? '_blank' : null })
      .run();
    setOpen(false);
  };

  const handleRemove = () => {
    editor.chain().focus().unsetLink().run();
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleApply();
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={t('bubble.link')}
              aria-pressed={isActive}
              data-testid="rich-text-bubble-link"
              className={cn(
                'h-7 w-7 min-h-11 min-w-11 md:min-h-0 md:min-w-0 transition-colors duration-100',
                isActive && 'bg-primary/10 text-primary',
              )}
            >
              <Link className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {t('bubble.link')}
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        className="w-72 p-3"
        data-testid="rich-text-link-popover"
        side="top"
        align="start"
      >
        <div className="flex flex-col gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('link.url')}
            data-testid="rich-text-link-url-input"
            className="h-8 text-sm"
            autoFocus
          />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={openInNewTab}
              onCheckedChange={(checked) => setOpenInNewTab(checked === true)}
            />
            {t('link.openNewTab')}
          </label>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={handleApply}
              disabled={!url.trim()}
              data-testid="rich-text-link-apply"
            >
              {t('link.apply')}
            </Button>
            {isActive && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={handleRemove}
                data-testid="rich-text-link-remove"
              >
                <Trash2 className="h-3 w-3" />
                {t('link.remove')}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
